import { useState, useEffect } from "react";
import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

interface Planimetry {
    planimetriaImageUrl: string | "";
    planimetriaDocumentUrl: string | "";
}

export interface PanoramicImage {
    id: number;
    urlBig: string;
    urlSmall: string;
    caption: string | null;
    position: number;
}

export interface SeasonalPrice {
    mese: string;
    anno: string;
    periodo_dal: string;
    periodo_al: string;
    tipo_periodo: 1 | 2 | 3 | 4; // 1: giornaliero, 2: settimanale, 3: quindicinale, 4: mensile
    prezzo: number;
    affittato: 0 | 1; // 0: non affittato, 1: affittato
}

interface PropertyDetails {
    id: number;
    agencyId: number;
    seoString: string;
    statusId: number;
    siteClicks?: number;
    descriptionDetails: {
        descriptionId: number;
        description: string;
        descriptionLang: string;
        descriptionTypeId: number;
        descriptionTypeName: string;
    }[];
    propertyStatusDetails: {
        propertyStatusId: number;
        propertyStatus: string;
        orderVisu: number;
    };
    performanceDetails: {
        summer: string;
        winter: string;
    };
    monthlyExpenses: string;
    contractDetails: {
        contractId: number;
        contractName: string;
    };
    auctionDetails: {
        auction: boolean;
        date: number;
        expired: boolean;
    };
    imagesDetails: {
        id: number;
        urlSmall: string | null;
        urlBig: string | null;
        urlLogo: string | null;
        main: boolean;
    }[];
    typologyDetails: {
        typologyId: number;
        it: string;
        en: string;
        de: string;
        fr: string;
        nl: string;
        es: string;
        ru: string;
        sq: string;
        zh: string;
        ja: string;
        seoString: string;
    };
    price: number;
    sqmt?: number;
    sqmtCommercial?: number;
    planimetry?: Planimetry[];
    ref: string;
    category: number;
    exclusive?: boolean;
    negotiable?: boolean;
    reservedNegotiation?: boolean;
    enerClassDetails?: {
        enerClassId: number;
        enerClassName: string;
        notes: string;
        orderVisu: number;
        afterOct2015: boolean;
    };
    address?: {
        id: number;
        propertyId: number;
        address: string;
        province: string;
        provinceAcronym: string;
        city: string;
        citySeoString: string;
        cityPostalCode: string;
        districtName: string;
        districtId: number;
        districtSeoString: string;
        fractionName: string;
        fractionId: number;
        fractionSeoString: string;
        latitude: number;
        longitude: number;
        googleAddress: string;
    };
    //RESIDENZIALI
    residential?: {
        rooms: string;
        kingSizeBedRooms: number;
        doubleBedRooms: number;
        singleBedRooms: number;
        utilityRoom: boolean;
        utilityRoomSqmt: number;
        heatingDetails: {
            heatingId: number;
            heatingName: string | null;
            heatingFuelId: number;
            heatingFuelName: string | null;
            heatingTypeId: number;
            heatingTypeName: string | null;
        };
        kitchenDetails: {
            kitchenId: number;
            kitchenName: string | null;
        };
        carBoxDetails: {
            carBoxId: number | null;
            carBoxName: string | null;
        };
        carBoxSqmt: number | null;
        carPlaces: number | null;
        coveredCarPlaces: boolean;
        unclampedCarPlaces: boolean;
        garageDetails: {
            garage1: boolean;
            garage1Sqmt: number;
            garage2: boolean;
            garage2Sqmt: number;
        };
        bathrooms: number;
        bathroomCharacteristics: string | null;
        doubleBathrooms: number;
        gardenDetails: {
            garden: string | null;
            privateGarden: boolean;
            privateGardenSqmt: number;
            condominiumGarden: boolean;
            condominiumGardenSqmt: number;
            otherLand: string | null;
            land: boolean;
            landSqmt: number;
        };
        conditionerDetails: {
            conditioner: boolean;
            otherConditioner: string | null;
        };
        furnitureDetails: {
            furnitureId: number;
            furnitureName: string | null;
            furnitureNotes: string | null;
        };
        terraceDetails: {
            terrace: boolean;
            terracesCharacteristics: string | null;
        }
        balcony: boolean;
        balconyCharacteristics: string | null;
        livingRoomDetails: {
            livingRoom: boolean;
            livingRoomSqmt: number;
        };
        diningRoomDetails: {
            diningRoom: boolean;
            diningRoomSqmt: number;
        };
        hallDetails: {
            hall: boolean;
            hallSqmt: number;
        };
        studioDetails: {
            studio: boolean;
            studioSqmt: number;
        };
        tavernDetails: {
            tavern: boolean;
            tavernSqmt: number;
        };
        mansardDetails: {
            mansard: boolean;
            mansardSqmt: number;
        };
        garretDetails: {
            garret: boolean;
            practicableGarret: boolean;
        };
        lastFloor: boolean;
        entryDetails: {
            indipendentEntry: boolean;
            traditionalEntry: boolean;
        };
        externalSpaces: string | null;
        areaDetails: {
            dayArea: string | null;
            nightArea: string | null;
        };
        bedPlacesDetails: {
            bedPlaces: number;
            bedPlacesDetails: string | null;
        };
        prestigeProperty: boolean;
        animalsAllowed: boolean;
        studentsAllowed: boolean;
        autoclave: boolean;
        alarm: boolean;
        waterSoftener: boolean;
        mosquitoNet: boolean;
        strongDoor: boolean;
        videophone: boolean;
        photovoltaicDetails: {
            photovoltaic: boolean;
            photovoltaicKw: number;
        };
        solarPanelsDetails: {
            solarPanels: boolean;
            solarPanelsKw: number;
        };
        miniWindTurbine: boolean;
        fireplace: boolean;
        swimmingPool: boolean;
        stonesDetails: {
            stones: boolean;
            stonesSqmt: number;
        };
        patioDetails: {
            patio: boolean;
            patioSqmt: number;
        };
    };
    floorDetails?: {
        floorId: number;
        floor: string;
        labelApp: string;
        orderVisu: number;
    };
    floors?: number;
    ipe?: number;
    apeDetails: {
        apeAfterOct2015: boolean;
        apeRin: string;
    };
    premises?: number;
    verifiedProperty?: boolean;
    //TERRENI
    serviceDetails?: {
        electricityAvailable?: number;
        gasAvailability?: number;
        roadAccess?: number;
        sewerSystem?: number;
        sidewalk?: number;
        telephoneLine?: number;
        waterAvailability?: number;
        publicLightning?: number;
    } | null;
    seasonalPrices?: SeasonalPrice[];
    panoramicImages?: PanoramicImage[];
}

interface UseGetPropertyOptions {
    status?: number;
}

export const useGetProperty = (propertyId: number | null, options: UseGetPropertyOptions = {}) => {
    const [propertyDetails, setPropertyDetails] = useState<PropertyDetails | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {

        if (!propertyId) {
            setPropertyDetails(null);
            setLoading(false);
            return;
        }

        const fetchPropertyDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                const requestBody = {
                    propertyId,
                    limit: 1,
                    offset: 0,
                    status: options.status
                };

                if (requestBody.status === undefined) {
                    delete (requestBody as Partial<typeof requestBody>).status;
                }

                const response = await axios.post(`${API_BASE_URL}/property`, requestBody);

                const property = response.data;
                if (!property || !property.propertyId) {
                    throw new Error("Dati della proprietà non validi.");
                }

                const detailDescriptions = Array.isArray(property.descriptionDetails) ? property.descriptionDetails : [];
                const alternateDescriptions = Array.isArray(property.descriptions)
                    ? property.descriptions.map((description: any, index: number) => ({
                        descriptionId: description.descriptionId ?? index,
                        description: description.description ?? description.text ?? "",
                        descriptionLang: description.descriptionLang ?? description.langId ?? "",
                        descriptionTypeId: description.descriptionTypeId ?? 1,
                        descriptionTypeName: description.descriptionTypeName ?? description.typeName ?? "",
                    }))
                    : [];
                const descriptions = [
                    ...detailDescriptions,
                    ...alternateDescriptions,
                    ...(typeof property.description === "string" && property.description
                        ? [{
                            descriptionId: 0,
                            description: property.description,
                            descriptionLang: "",
                            descriptionTypeId: 1,
                            descriptionTypeName: "",
                        }]
                        : []),
                ];
                const images = Array.isArray(property.imagesDetails) ? property.imagesDetails : [];

                const formattedProperty: PropertyDetails = {
                    id: property.propertyId,
                    agencyId: property.agencyId,
                    seoString: property.seoString,
                    siteClicks: property.clicksFromSite,
                    floorDetails: {
                        floorId: property.floorDetails?.floorId || 0,
                        floor: property.floorDetails?.floor || "",
                        labelApp: property.floorDetails?.labelApp || "",
                        orderVisu: property.floorDetails?.orderVisu || 0,
                    },
                    auctionDetails: {
                        auction: property.auctionDetails?.auction || false,
                        date: property.auctionDetails?.date || 0,
                        expired: property.auctionDetails?.expired ?? true,
                    },
                    floors: property.floors,
                    ipe: property.ipe || "0.00",
                    apeDetails: {
                        apeAfterOct2015: property.apeDetails?.apeAfterOct2015 || false,
                        apeRin: property.apeDetails?.apeRin || "0.00",
                    },
                    premises: property.premises,
                    verifiedProperty: property.verifiedProperty,
                    descriptionDetails: descriptions.map((desc: { descriptionId: any; description: any; descriptionLang: any; descriptionTypeId: any; descriptionTypeName: any; }) => ({
                        descriptionId: desc.descriptionId,
                        description: desc.description,
                        descriptionLang: desc.descriptionLang,
                        descriptionTypeId: desc.descriptionTypeId,
                        descriptionTypeName: desc.descriptionTypeName,
                    })),
                    imagesDetails: images.map((img: any) => {
                        return {
                            ...img,
                        };
                    }),
                    statusId: property.statusId,
                    propertyStatusDetails: {
                        propertyStatusId: property.propertyStatusDetails?.propertyStatusId || 0,
                        propertyStatus: property.propertyStatusDetails?.propertyStatus || "",
                        orderVisu: property.propertyStatusDetails?.orderVisu || 0,
                    },
                    performanceDetails: {
                        summer: property.performanceDetails?.summer || "",
                        winter: property.performanceDetails?.winter || "",
                    },
                    monthlyExpenses: property.monthlyExpenses || "",
                    contractDetails: {
                        contractId: property.contractDetails?.contractId || 0,
                        contractName: property.contractDetails?.contractName || "",
                    },
                    typologyDetails: {
                        typologyId: property.typologyDetails?.typologyId || 0,
                        it: property.typologyDetails?.it || "",
                        en: property.typologyDetails?.en || "",
                        de: property.typologyDetails?.de || "",
                        fr: property.typologyDetails?.fr || "",
                        nl: property.typologyDetails?.nl || "",
                        es: property.typologyDetails?.es || "",
                        ru: property.typologyDetails?.ru || "",
                        sq: property.typologyDetails?.sq || "",
                        zh: property.typologyDetails?.zh || "",
                        ja: property.typologyDetails?.ja || "",
                        seoString: property.typologyDetails?.seoString || "",
                    },
                    price: property.price,
                    serviceDetails: property.category === 4 ? {
                        electricityAvailable: property.serviceDetails?.electricityAvailable || 0,
                        gasAvailability: property.serviceDetails?.gasAvailability || 0,
                        roadAccess: property.serviceDetails?.roadAccess || 0,
                        sewerSystem: property.serviceDetails?.sewerSystem || 0,
                        sidewalk: property.serviceDetails?.sidewalk || 0,
                        telephoneLine: property.serviceDetails?.telephoneLine || 0,
                        waterAvailability: property.serviceDetails?.waterAvailability || 0,
                        publicLightning: property?.publicLightning || 0,
                    } : null,
                    sqmt: property.sqmt,
                    sqmtCommercial: property.sqmtCommercial,
                    planimetry: property.planimetry,
                    ref: property.ref,
                    category: property.category,
                    exclusive: property.esclusiva,
                    reservedNegotiation: property.trattativa_riservata,
                    negotiable: property.trattabile,
                    enerClassDetails: {
                        enerClassId: property.enerClassDetails?.enerClassId || 0,
                        enerClassName: property.enerClassDetails?.enerClassName || "",
                        notes: property.enerClassDetails?.notes || "",
                        orderVisu: property.enerClassDetails?.orderVisu || 0,
                        afterOct2015: property.enerClassDetails?.afterOct2015 || false,
                    },
                    residential: property.residential ?? {
                        rooms: "",
                        doubleBedRooms: 0,
                        utilityRoom: 0,
                        utilityRoomSqmt: 0,
                        descriptionDetails: descriptions.map((desc: { descriptionId: any; description: any; descriptionTypeId: any; descriptionTypeName: any; }) => ({
                            descriptionId: desc.descriptionId,
                            description: desc.description,
                            descriptionTypeId: desc.descriptionTypeId,
                            descriptionTypeName: desc.descriptionTypeName,
                        })),
                        heatingDetails: {
                            heatingId: 0,
                            heatingName: "",
                            heatingFuelId: 0,
                            heatingFuelName: "",
                            heatingTypeId: "",
                            heatingTypeName: "",
                        },
                        kitchenDetails: {
                            kitchenId: 0,
                            kitchenName: "",
                        },
                        carBoxDetails: {
                            carBoxId: property.carBox || 0,
                            carBoxName: property.carBoxDetails?.[0]?.carBoxName || null,
                        },
                        carPlaces: property.residential?.carPlaces || 0,
                        bathrooms: 0,
                        furnitureDetails: {
                            furnitureId: 0,
                            furnitureName: "",
                            furnitureNotes: null,
                        },
                        terraceDetails: {
                            terrace: 0,
                            terracesCharacteristics: null,
                        },
                        alarm: false,
                        mosquitoNet: false,
                        strongDoor: false,
                        videophone: false,
                        garden: null,
                        gardenDetails: {
                            garden: null,
                            privateGarden: false,
                            privateGardenSqmt: 0,
                            condominiumGarden: false,
                            condominiumGardenSqmt: 0,
                            otherLand: null,
                            land: false,
                            landSqmt: 0,
                        },
                        conditionerDetails: {
                            conditioner: false,
                            otherConditioner: null,
                        },
                        fireplace: false,
                        swimmingPool: false,
                        airConditioner: 0,
                        livingRoom: 0,
                        patioDetails: {
                            patio: false,
                            patioSqmt: 0,
                        },
                        entryDetails: {
                            indipendentEntry: false,
                            traditionalEntry: false,
                        }
                    },
                    address: {
                        address: property.indirizzo?.indirizzo || "",
                        districtName: property.indirizzo?.quartiereName || "",
                        districtId: property.indirizzo?.quartiereId || 0,
                        districtSeoString: property.indirizzo?.quartiereSeoString || "",
                        city: property.indirizzo?.comuneIt || "",
                        citySeoString: property.indirizzo?.comuneSeoString || "",
                        cityPostalCode: property.indirizzo?.cap || "",
                        fractionName: property.indirizzo?.frazioneName || "",
                        fractionId: property.indirizzo?.frazioneId || "",
                        fractionSeoString: property.indirizzo?.frazioneSeoString || "",
                        googleAddress: property.indirizzo?.indirizzo_google || "",
                        id: property.indirizzo?.id || 0,
                        latitude: property.indirizzo?.lat || null,
                        longitude: property.indirizzo?.lng || null,
                        propertyId: property.indirizzo?.property_id || null,
                        province: property.indirizzo?.provincia || "",
                        provinceAcronym: property.indirizzo?.provinciaSigla || "",
                    },
                    seasonalPrices: property.seasonalPrices,
                    panoramicImages: property.panoramicImages,
                };
                setPropertyDetails(formattedProperty);

            } catch (err) {
                setError(err instanceof Error ? err.message : "Errore sconosciuto");
            } finally {
                setLoading(false);
            }
        };
        fetchPropertyDetails();
    }, [propertyId, options.status]);
    return { propertyDetails, loading, error };
};